/**
 * /admin/ingest/players — country-wise player ingest + duplicate-review
 * queue for the unified admin.
 *
 * Sections:
 *   1. Country tiles — pending-candidate count + total players in DB
 *      per country, sorted by pending desc. Click a tile to filter the
 *      candidate list below to that country.
 *   2. Recent uploads — last 8 PLAYER_PROFILE IngestJob rows with their
 *      parse summary (parsed / new / match / no_delta) + commit summary.
 *   3. Candidate review — table of PlayerCandidate rows with the
 *      extracted name + ranked DB matches. Per-row actions: Match
 *      (pick one), Create new, Skip.
 *
 * Backend endpoints (superuser-gated):
 *   GET  /api/admin/v2/ingest/players/countries/
 *   GET  /api/admin/v2/ingest/players/candidates/?country=&decision=&search=
 *   POST /api/admin/v2/ingest/players/candidates/<id>/decide/
 *   GET  /api/admin/v2/ingest/players/uploads/?country=
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, Globe2, Users, UploadCloud, Check, X, Plus,
  ExternalLink, AlertTriangle, Search, FileText, Loader2, Trash2,
  PlayCircle,
} from "lucide-react";

import {
  listPlayerCountries,
  listPlayerCandidates,
  decidePlayerCandidate,
  listPlayerIngestUploads,
  deletePlayerIngestUpload,
  deletePlayerCountryBatch,
  listPlayerOwners,
} from "../../api/ingest.admin.api";
import {
  uploadFileJobWithProgress, extractJob, getJob, commitJob,
} from "../../api/ingest.api";

const DECISION_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "MATCHED", label: "Matched" },
  { value: "NEW",     label: "Created new" },
  { value: "SKIPPED", label: "Skipped" },
];

const STATE_PILL = {
  UPLOADED:   "bg-slate-500/15 text-slate-300 border-slate-500/40",
  EXTRACTING: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  REVIEW:     "bg-amber-500/15 text-amber-300 border-amber-500/40",
  COMMITTED:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  DISCARDED:  "bg-zinc-600/15 text-zinc-400 border-zinc-600/40",
  ERRORED:    "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

export default function AdminIngestPlayers() {
  const [countries, setCountries] = useState([]);
  const [pendingUnattributed, setPendingUnattributed] = useState(0);
  const [uploads, setUploads] = useState([]);
  const [owners, setOwners] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candCount, setCandCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [decision, setDecision] = useState("PENDING");
  const [countryFilter, setCountryFilter] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [flash, setFlash] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(null);
  const [commitBusy, setCommitBusy] = useState(null);
  // 2026-07-09 — per-country batch delete state. countryDeleteTarget is
  // the country tile object once the operator taps the trash button;
  // countryConfirmText holds the typed confirmation (must equal the
  // country name exactly before the destructive button un-disables).
  const [countryDeleteTarget, setCountryDeleteTarget] = useState(null);
  const [countryConfirmText, setCountryConfirmText] = useState("");
  const [countryDeleteBusy, setCountryDeleteBusy] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const [c, u, o] = await Promise.all([
        listPlayerCountries(),
        listPlayerIngestUploads({ page_size: 8 }),
        listPlayerOwners(),
      ]);
      setCountries(c.data?.results || []);
      setPendingUnattributed(c.data?.pending_unattributed || 0);
      setUploads(u.data?.results || u.data || []);
      setOwners(o.data?.owners || []);
    } finally {
      setLoading(false);
    }
    await refreshCandidates();
  }

  async function refreshCandidates() {
    try {
      const params = { decision, page_size: 50 };
      if (countryFilter) params.country = countryFilter;
      if (search.trim()) params.search = search.trim();
      const { data } = await listPlayerCandidates(params);
      setCandidates(data?.results || []);
      setCandCount(data?.count || 0);
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    }
  }

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(refreshCandidates, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [decision, countryFilter, search]);

  async function handleDecide(candidate, decisionKey, payload = {}) {
    setBusyKey(candidate.id);
    setFlash(null);
    try {
      const body = { decision: decisionKey, ...payload };
      await decidePlayerCandidate(candidate.id, body);
      setFlash({
        ok: true,
        msg: `"${candidate.extracted_name}" → ${decisionKey.toLowerCase()}.`,
      });
      await Promise.all([refreshCandidates(), reloadCountriesOnly()]);
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCommitUpload(job) {
    setCommitBusy(job.id);
    setFlash(null);
    try {
      const res = await commitJob(job.id);
      const created = res?.summary?.created ?? 0;
      const updated = res?.summary?.updated_rows ?? 0;
      const errs = res?.summary?.errors ?? 0;
      const parts = [];
      if (created) parts.push(`${created} added`);
      if (updated) parts.push(`${updated} updated`);
      if (errs) parts.push(`${errs} errored`);
      setFlash({
        ok: true,
        msg: `Committed "${job.source_filename || job.id.slice(0, 8)}" — ${parts.join(", ") || "no changes"}.`,
      });
      await loadAll();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setCommitBusy(null);
    }
  }

  async function handleDeleteUpload(job) {
    setDeleteBusy(job.id);
    setFlash(null);
    try {
      const { data } = await deletePlayerIngestUpload(job.id);
      const parts = [];
      if (data.players_deleted) parts.push(`${data.players_deleted} player${data.players_deleted === 1 ? "" : "s"} deleted`);
      if (data.players_skipped_in_use) parts.push(`${data.players_skipped_in_use} kept (in use)`);
      if (data.players_skipped_safety) parts.push(`${data.players_skipped_safety} kept (not SYSTEM)`);
      setFlash({
        ok: true,
        msg: `Discarded "${job.source_filename || job.id.slice(0, 8)}" — ${parts.join(", ") || "no players to clean up"}.`,
      });
      setDeleteTarget(null);
      await loadAll();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setDeleteBusy(null);
    }
  }

  // 2026-07-09 — batch delete every SYSTEM-owned Player under a
  // country. Backend keeps linked_user + in-use rows; discards any
  // PLAYER_PROFILE IngestJob whose filename mentions the country name.
  async function handleDeleteCountry(country) {
    if (!country) return;
    setCountryDeleteBusy(true);
    setFlash(null);
    try {
      const { data } = await deletePlayerCountryBatch(country.code);
      const parts = [];
      if (data.players_hard_deleted) parts.push(`${data.players_hard_deleted} hard-deleted`);
      if (data.players_soft_deleted) parts.push(`${data.players_soft_deleted} tombstoned (kept for completed matches)`);
      if (data.in_progress_matches_deleted) parts.push(`${data.in_progress_matches_deleted} in-progress match${data.in_progress_matches_deleted === 1 ? "" : "es"} deleted`);
      if (data.jobs_discarded) parts.push(`${data.jobs_discarded} upload${data.jobs_discarded === 1 ? "" : "s"} discarded`);
      setFlash({
        ok: true,
        msg: `Cleared ${country.name} — ${parts.join(", ") || "nothing to clean up"}.`,
      });
      setCountryDeleteTarget(null);
      setCountryConfirmText("");
      await loadAll();
    } catch (e) {
      setFlash({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setCountryDeleteBusy(false);
    }
  }

  async function reloadCountriesOnly() {
    try {
      const c = await listPlayerCountries();
      setCountries(c.data?.results || []);
      setPendingUnattributed(c.data?.pending_unattributed || 0);
    } catch { /* tile counts are non-critical — silent on retry */ }
  }

  const totalPending = useMemo(
    () => countries.reduce((s, c) => s + (c.pending_candidates || 0), 0) + pendingUnattributed,
    [countries, pendingUnattributed],
  );

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="w-6 h-6 text-emerald-400" />
            Player ingest
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Bulk-load player profiles country-wise, review duplicates, approve fuzzy matches.
            Drop a <code className="px-1 bg-bg-secondary rounded">players_bulk_&lt;Country&gt;_&lt;Gender&gt;.zip</code> (or a single <code className="px-1 bg-bg-secondary rounded">.xlsx</code>) in the uploader below.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/cricket/players?has_ci_id=true&all_tenants=true"
            className="px-3 py-2 text-sm rounded-md border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1.5"
            title="Browse every ingested player profile (ING-tagged) in the Players tab — across all owner scopes"
          >
            <Users className="w-4 h-4" /> Browse ingested players
          </Link>
          <button
            onClick={loadAll}
            className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      <PlayerProfileUploader onComplete={loadAll} />


      {flash && (
        <div className={`rounded-md border p-3 text-sm flex items-start gap-2 ${
          flash.ok
            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
            : "border-rose-500/40 bg-rose-500/10 text-rose-300"
        }`}>
          {flash.ok ? <Check className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div className="min-w-0">{flash.msg}</div>
        </div>
      )}

      {/* Country tiles */}
      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-widest text-text-secondary flex items-center gap-2">
          <Globe2 className="w-3.5 h-3.5" />
          By country
          <span className="text-text-secondary/60">
            · {totalPending.toLocaleString("en-IN")} pending candidate{totalPending === 1 ? "" : "s"} total
          </span>
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {loading && countries.length === 0 && (
            <div className="col-span-full text-text-secondary text-sm">Loading countries…</div>
          )}
          {countries.map((c) => {
            const active = countryFilter === c.code;
            const browseHref = `/cricket/players?has_ci_id=true&all_tenants=true&nationality=${encodeURIComponent(c.code)}`;
            return (
              <div
                key={c.id}
                className={`text-left rounded-lg border p-3 transition ${
                  active
                    ? "border-button-primary bg-button-primary/10"
                    : "border-border-default hover:bg-bg-secondary"
                }`}
              >
                <button
                  onClick={() => setCountryFilter(active ? "" : c.code)}
                  className="block w-full text-left"
                  title={`${c.name} — click to filter the review queue below`}
                >
                  <div className="text-sm font-semibold flex items-center justify-between">
                    <span className="truncate">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-text-secondary">{c.code}</span>
                  </div>
                  <div className="mt-1 flex items-end justify-between text-xs">
                    <div>
                      <div className="font-mono text-text-primary">{c.total_players_in_db.toLocaleString("en-IN")}</div>
                      <div className="text-text-secondary">ingested</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-mono ${c.pending_candidates ? "text-amber-300" : "text-text-secondary"}`}>
                        {c.pending_candidates.toLocaleString("en-IN")}
                      </div>
                      <div className="text-text-secondary">pending</div>
                    </div>
                  </div>
                  {c.last_player_ingest_at && (
                    <div className="mt-1 text-[10px] text-text-secondary truncate">
                      last upload: {new Date(c.last_player_ingest_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  )}
                </button>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <Link
                    to={browseHref}
                    className="inline-flex items-center gap-1 text-[10px] text-amber-300 hover:text-amber-200 hover:underline"
                    title={`Browse ingested ${c.name} players in the Players tab`}
                  >
                    Browse ingested {c.name} <ExternalLink className="w-3 h-3" />
                  </Link>
                  {/* 2026-07-09 — per-country batch delete. Opens a
                      typed-confirmation modal (operator must type the
                      country name) before the hard-delete fires. Gated
                      on total_players_in_db > 0 to hide the button for
                      empty tiles the operator never actually loaded. */}
                  {c.total_players_in_db > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCountryDeleteTarget(c);
                        setCountryConfirmText("");
                      }}
                      title={`Delete every ingested ${c.name} player profile`}
                      className="inline-flex items-center gap-1 text-[10px] text-rose-400 hover:text-rose-300 hover:underline"
                    >
                      <Trash2 className="w-3 h-3" /> Delete batch
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Players by owner */}
      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-widest text-text-secondary flex items-center gap-2">
          <Users className="w-3.5 h-3.5" />
          Players by owner
          <span className="text-text-secondary/60">
            · {owners.length} tenant{owners.length === 1 ? "" : "s"} holding non-SYSTEM rows
          </span>
        </h2>
        <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <tr>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Total players</th>
                <th className="px-3 py-2 text-right">Ingested (ING)</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {owners.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-secondary text-sm">No non-SYSTEM owners yet.</td></tr>
              )}
              {owners.map((o) => {
                const browseHref = `/cricket/players?all_tenants=true&owner_type=${o.owner_type}&owner_id=${encodeURIComponent(o.owner_id)}`;
                return (
                  <tr key={`${o.owner_type}:${o.owner_id}`} className="border-t border-border-default">
                    <td className="px-3 py-2">
                      <div className="font-medium">{o.display || "(unknown)"}</div>
                      {o.email && (
                        <div className="text-text-secondary text-xs">{o.email}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${
                        o.owner_type === "ORG"
                          ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/40"
                          : "bg-blue-500/15 text-blue-300 border-blue-500/40"
                      }`}>
                        {o.owner_type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{o.player_count.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-300">{o.ingested_count.toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={browseHref}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs"
                      >
                        View players
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent uploads */}
      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-widest text-text-secondary">
          Recent player-profile uploads
        </h2>
        <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <tr>
                <th className="px-3 py-2">File</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right" title="Total player rows parsed from the file">Total</th>
                <th className="px-3 py-2 text-right" title="Players that did not exist before this upload">New</th>
                <th className="px-3 py-2 text-right" title="Players that were already in the database (with or without fill-in updates)">Existing</th>
                <th className="px-3 py-2 text-right" title="Players actually added to the database after commit">Added</th>
                <th className="px-3 py-2 text-right">Uploaded</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-text-secondary text-sm">No PLAYER_PROFILE uploads yet.</td></tr>
              )}
              {uploads.map((u) => {
                const s = u.summary || {};
                const c = u.commit_summary || {};
                const total = s.parsed || 0;
                const newCount = s.new_count || 0;
                const existing = (s.match_count || 0) + (s.no_delta_count || 0);
                const added = c.created || 0;
                const isBusy = deleteBusy === u.id;
                return (
                  <tr key={u.id} className="border-t border-border-default">
                    <td className="px-3 py-2">
                      <Link
                        to={`/ingest/${u.id}`}
                        className="text-text-primary hover:text-button-primary inline-flex items-center gap-1"
                      >
                        {u.source_filename || "(no name)"}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${STATE_PILL[u.state] || "border-border-default text-text-secondary"}`}>
                        {STATE_LABEL[u.state] || u.state}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{total}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300">{newCount}</td>
                    <td className="px-3 py-2 text-right font-mono text-text-secondary">{existing}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-300">{added}</td>
                    <td className="px-3 py-2 text-right text-text-secondary text-xs">
                      {new Date(u.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                        {u.state === "REVIEW" && (
                          <button
                            type="button"
                            disabled={commitBusy === u.id}
                            onClick={() => handleCommitUpload(u)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Add these players to the database now"
                          >
                            {commitBusy === u.id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <PlayCircle className="w-3 h-3" />}
                            {commitBusy === u.id ? "Adding…" : "Add players"}
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={isBusy || u.state === "DISCARDED"}
                          onClick={() => setDeleteTarget(u)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                          title={u.state === "DISCARDED" ? "Already discarded" : "Delete this upload and every SYSTEM player it created"}
                        >
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {deleteTarget && (
        <DeleteUploadModal
          job={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDeleteUpload(deleteTarget)}
          busy={deleteBusy === deleteTarget.id}
        />
      )}

      {countryDeleteTarget && (
        <DeleteCountryModal
          country={countryDeleteTarget}
          confirmText={countryConfirmText}
          onConfirmTextChange={setCountryConfirmText}
          onCancel={() => { setCountryDeleteTarget(null); setCountryConfirmText(""); }}
          onConfirm={() => handleDeleteCountry(countryDeleteTarget)}
          busy={countryDeleteBusy}
        />
      )}

      {/* Candidate review */}
      <section className="space-y-2">
        <h2 className="text-xs uppercase tracking-widest text-text-secondary flex items-center gap-2">
          Candidate review queue
          <span className="text-text-secondary/60">· {candCount.toLocaleString("en-IN")} total</span>
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-text-secondary" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by extracted name…"
              className="pl-8 pr-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary w-72"
            />
          </div>
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            className="px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary"
          >
            {DECISION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {countryFilter && (
            <button
              onClick={() => setCountryFilter("")}
              className="px-2 py-1 text-xs rounded-md border border-button-primary/60 text-button-primary"
            >
              Country: {countryFilter} ✕
            </button>
          )}
        </div>

        <div className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-table-header text-left text-[11px] uppercase tracking-widest text-text-secondary">
              <tr>
                <th className="px-3 py-2">Extracted name</th>
                <th className="px-3 py-2">Top suggested matches</th>
                <th className="px-3 py-2">From job</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-text-secondary text-sm">
                  {decision === "PENDING"
                    ? "No pending candidates — nothing to review right now."
                    : `No candidates in state ${decision}.`}
                </td></tr>
              )}
              {candidates.map((c) => (
                <tr key={c.id} className="border-t border-border-default align-top">
                  <td className="px-3 py-2 font-medium">{c.extracted_name}</td>
                  <td className="px-3 py-2 space-y-1">
                    {(c.candidates || []).length === 0 && (
                      <div className="text-text-secondary text-xs">No matches found — likely a new player.</div>
                    )}
                    {(c.candidates || []).slice(0, 5).map((m, i) => (
                      <div key={m.player_id || i} className="flex items-center justify-between gap-2 text-xs">
                        <div className="truncate">
                          <span className="text-text-primary">{m.name}</span>
                          {typeof m.distance !== "undefined" && (
                            <span className="text-text-secondary ml-2">
                              {(m.distance * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        {c.decision === "PENDING" && (
                          <button
                            disabled={busyKey === c.id}
                            onClick={() => handleDecide(c, "MATCHED", { chosen_player_id: m.player_id })}
                            className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[11px] disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Match
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 text-xs text-text-secondary">
                    <Link to={`/ingest/${c.job_id}`} className="hover:text-button-primary inline-flex items-center gap-1">
                      {c.job_filename || c.job_id.slice(0, 8)}
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                    <div className="text-text-secondary/70 mt-0.5">
                      {new Date(c.job_uploaded_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                    {c.decision === "PENDING" ? (
                      <>
                        <button
                          disabled={busyKey === c.id}
                          onClick={() => handleDecide(c, "NEW")}
                          className="px-2 py-1 rounded border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-xs disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" /> Create new
                        </button>
                        <button
                          disabled={busyKey === c.id}
                          onClick={() => handleDecide(c, "SKIPPED")}
                          className="px-2 py-1 rounded border border-border-default text-text-secondary hover:bg-bg-secondary text-xs disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          <X className="w-3 h-3" /> Skip
                        </button>
                      </>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                        {c.decision}
                        {c.decided_by_email ? ` · ${c.decided_by_email}` : ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}


// ─── Upload card ─────────────────────────────────────────────────────
// Drag-drop / file-pick uploader with an actual progress bar (XHR
// onUploadProgress) and verbose error surfacing. After the upload
// returns the job id, we kick off extract synchronously and poll the
// job state so the operator can see "EXTRACTING -> REVIEW -> COMMITTED"
// without leaving the page. Refresh hooks back into the parent so the
// candidate queue + country tiles re-populate when the job lands.

const MAX_UPLOAD_BYTES = 200 * 1024 * 1024; // 200 MB

const STATE_LABEL = {
  UPLOADED:   "Uploaded",
  EXTRACTING: "Reading file",
  REVIEW:     "Ready to add",
  COMMITTED:  "Added",
  ERRORED:    "Failed",
  DISCARDED:  "Discarded",
};

function PlayerProfileUploader({ onComplete }) {
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle / uploading / extracting / polling / review / committed / error
  const [progress, setProgress] = useState(0);
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  function reset() {
    setFile(null);
    setPhase("idle");
    setProgress(0);
    setJob(null);
    setError(null);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (inputRef.current) inputRef.current.value = "";
  }

  function readableError(e) {
    // axios error → pull the most useful field out of the response.
    if (!e) return "Unknown error";
    const r = e.response;
    if (r) {
      const status = r.status;
      const data = r.data;
      if (data && typeof data === "object") {
        if (data.detail) return `HTTP ${status}: ${data.detail}`;
        if (data.error) return `HTTP ${status}: ${data.error}`;
        // DRF field-level errors → flatten.
        const flat = Object.entries(data)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join("; ");
        if (flat) return `HTTP ${status}: ${flat}`;
        return `HTTP ${status}: ${JSON.stringify(data).slice(0, 300)}`;
      }
      if (typeof data === "string" && data.trim()) {
        return `HTTP ${status}: ${data.slice(0, 300)}`;
      }
      return `HTTP ${status} (no response body)`;
    }
    return e.message || String(e);
  }

  async function startUpload() {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — over the ${MAX_UPLOAD_BYTES / 1024 / 1024} MB cap. Split it into smaller zips.`);
      return;
    }
    setPhase("uploading");
    setProgress(0);
    setError(null);
    try {
      const created = await uploadFileJobWithProgress(
        "PLAYER_PROFILE", file,
        (p) => { setProgress(p.percent ?? 0); },
      );
      setJob(created);
      setPhase("extracting");
      // Kick off extract synchronously; backend runs the deterministic
      // parser in-request. Poll in case the response is delayed by a
      // long zip with hundreds of xlsx entries.
      pollRef.current = setInterval(async () => {
        try {
          const j = await getJob(created.id);
          setJob(j);
          if (j.state === "REVIEW") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPhase("review");
            if (onComplete) onComplete();
          } else if (j.state === "ERRORED") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(j.error || "Extract reported an error.");
            setPhase("error");
          } else if (j.state === "COMMITTED") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setPhase("committed");
            if (onComplete) onComplete();
          }
        } catch (e) { /* keep polling on transient errors */ }
      }, 2000);
      try {
        const after = await extractJob(created.id);
        setJob(after);
        if (after.state === "REVIEW") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPhase("review");
          if (onComplete) onComplete();
        }
      } catch (e) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setError(readableError(e));
        setPhase("error");
      }
    } catch (e) {
      setError(readableError(e));
      setPhase("error");
    }
  }

  function onDrop(e) {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }

  const showPickArea = phase === "idle" || phase === "error";
  const summary = job?.extracted?.summary;

  return (
    <section className="space-y-2">
      <h2 className="text-xs uppercase tracking-widest text-text-secondary flex items-center gap-2">
        <UploadCloud className="w-3.5 h-3.5" />
        Upload PLAYER_PROFILE
      </h2>
      <div className="rounded-lg border border-border-default bg-bg-primary p-4 space-y-3">
        {showPickArea && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-emerald-500/30 hover:border-emerald-500/60 rounded-md p-5 cursor-pointer transition-colors"
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.zip"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <div className="flex items-center gap-3 text-sm">
                <FileText className="w-5 h-5 text-emerald-300 shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-text-primary truncate">{file.name}</div>
                  <div className="text-text-secondary text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="ml-auto text-xs text-text-secondary hover:text-text-primary"
                >
                  Replace
                </button>
              </div>
            ) : (
              <div className="text-center text-sm text-emerald-300">
                <UploadCloud className="w-5 h-5 inline-block mr-2 -mt-0.5" />
                Drop a <code className="px-1 bg-bg-secondary rounded">.zip</code> of player xlsx files (or a single <code className="px-1 bg-bg-secondary rounded">.xlsx</code>) here, or click to pick.
                <div className="text-text-secondary text-xs mt-1">
                  Expected name pattern: <code>players_bulk_&lt;Country&gt;_&lt;Gender&gt;/&lt;Country&gt;_&lt;Name&gt;_&lt;ci_player_id&gt;.xlsx</code>
                </div>
              </div>
            )}
          </div>
        )}

        {phase === "uploading" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-300" />
              <span className="text-text-primary">Uploading <span className="font-mono">{file?.name}</span></span>
              <span className="ml-auto font-mono text-emerald-300">{progress}%</span>
            </div>
            <div
              role="progressbar"
              aria-label="Upload progress"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-2 rounded-full bg-bg-secondary overflow-hidden"
            >
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {(phase === "extracting" || phase === "polling") && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="w-4 h-4 animate-spin text-blue-300" />
              <span className="text-text-primary">
                {STATE_LABEL[job?.state] || "Parsing player rows"} — this can take 10–60s for big zips.
              </span>
            </div>
          </div>
        )}

        {phase === "review" && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2">
            <div className="flex items-center gap-2 text-emerald-200 font-semibold text-sm">
              <Check className="w-4 h-4" />
              Read the file — ready to add players
            </div>
            {summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Stat label="Total"    value={summary.parsed} />
                <Stat label="New"      value={summary.new_count} tone="emerald" />
                <Stat label="Existing" value={(summary.match_count || 0) + (summary.no_delta_count || 0)} />
                <Stat label="Skipped"  value={(summary.skipped_no_player_info || 0) + (summary.skipped_bad_filename || 0)} tone="rose" />
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Link
                to={`/ingest/${job.id}`}
                className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-500 text-white inline-flex items-center gap-1.5"
              >
                Open job &amp; add to library
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary"
              >
                Upload another
              </button>
            </div>
          </div>
        )}

        {phase === "committed" && (
          <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-emerald-200 text-sm">
            <Check className="w-4 h-4 inline-block mr-1" />
            Committed. Refreshing the queue…
            <button onClick={reset} className="ml-3 underline">Upload another</button>
          </div>
        )}

        {phase === "error" && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 space-y-2">
            <div className="text-rose-200 font-semibold text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Upload failed
            </div>
            <div className="text-xs whitespace-pre-wrap text-rose-200/90 font-mono">
              {error}
            </div>
            <button
              onClick={reset}
              className="px-3 py-1.5 text-sm rounded-md border border-rose-500/40 text-rose-200 hover:bg-rose-500/20"
            >
              Try again
            </button>
          </div>
        )}

        {showPickArea && (
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={startUpload}
              disabled={!file}
              className="px-3 py-1.5 text-sm rounded-md bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <UploadCloud className="w-4 h-4" /> Upload &amp; extract
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function DeleteUploadModal({ job, busy, onCancel, onConfirm }) {
  const createdCount = job.commit_summary?.created || 0;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Delete ingest upload"
        className="bg-bg-primary text-text-primary border border-rose-500/40 rounded-lg w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-border-default flex items-center gap-2 text-rose-300 font-semibold">
          <AlertTriangle className="w-4 h-4" />
          Delete this upload?
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div>
            <span className="font-mono">{job.source_filename || job.id.slice(0, 8)}</span>{" "}
            <span className="text-text-secondary text-xs">({job.state})</span>
          </div>
          <div className="rounded border border-border-default bg-bg-secondary/40 p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-text-secondary">Players created from this upload</span>
              <span className="font-mono">{createdCount}</span>
            </div>
          </div>
          <div className="text-xs text-text-secondary leading-relaxed">
            The upload row will be marked <span className="font-mono">DISCARDED</span> and every SYSTEM-owned Player it created
            will be permanently deleted. Players already used in a Match / Competition / Series are kept (the FK
            is PROTECTed); they appear in the response as "in use".
          </div>
          <div className="text-xs text-amber-300/90 leading-relaxed">
            This action is irreversible. Re-uploading the same file will create fresh rows with new UUIDs.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {busy ? "Deleting…" : "Delete upload"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteCountryModal({
  country, confirmText, onConfirmTextChange, busy, onCancel, onConfirm,
}) {
  // Operator must type the country name exactly to arm the destructive
  // button — same guardrail Vercel / GitHub use for repo delete flows.
  const confirmOk =
    confirmText.trim().toLowerCase() === (country.name || "").trim().toLowerCase();
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Delete every ingested ${country.name} player`}
        className="bg-bg-primary text-text-primary border border-rose-500/40 rounded-lg w-full max-w-md overflow-hidden"
      >
        <div className="px-5 py-3 border-b border-border-default flex items-center gap-2 text-rose-300 font-semibold">
          <AlertTriangle className="w-4 h-4" />
          Delete the entire {country.name} batch?
        </div>
        <div className="px-5 py-4 space-y-3 text-sm">
          <div className="rounded border border-border-default bg-bg-secondary/40 p-3 text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-text-secondary">Country</span>
              <span className="font-mono">{country.name} ({country.code})</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Ingested SYSTEM players</span>
              <span className="font-mono">{country.total_players_in_db?.toLocaleString?.("en-IN") ?? country.total_players_in_db}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Pending candidates</span>
              <span className="font-mono">{country.pending_candidates?.toLocaleString?.("en-IN") ?? country.pending_candidates}</span>
            </div>
          </div>
          <div className="text-xs text-text-secondary leading-relaxed">
            Every SYSTEM-owned {country.name} Player row is removed from the tile:
            <ul className="list-disc pl-4 mt-1 space-y-0.5">
              <li>Rows never used in a match are <span className="text-rose-300">hard-deleted</span>.</li>
              <li>Rows used in a <span className="font-mono">COMPLETED</span> match are <span className="text-rose-300">tombstoned</span> — renamed to "Deleted Player" and their nationality cleared so the scorecard's FKs stay intact (batting / bowling numbers survive; the name shows as "Deleted Player").</li>
              <li>Any <span className="font-mono">IN-PROGRESS</span> / <span className="font-mono">DRAFT</span> / <span className="font-mono">READY</span> match referencing a purged player is <span className="text-rose-300">deleted</span> outright (cascades to its balls / XI / squad).</li>
              <li><span className="font-mono">PLAYER_PROFILE</span> uploads matching {country.name} are soft-discarded (audit trail preserved).</li>
            </ul>
          </div>
          <div className="text-xs text-amber-300/90 leading-relaxed">
            This action is irreversible. Type the country name below to confirm.
          </div>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => onConfirmTextChange(e.target.value)}
            placeholder={`Type "${country.name}" to confirm`}
            disabled={busy}
            autoFocus
            className="w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-secondary font-mono"
          />
        </div>
        <div className="px-5 py-3 border-t border-border-default flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-md border border-border-default hover:bg-bg-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !confirmOk}
            className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            {busy ? "Deleting…" : `Delete ${country.name} batch`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const TONE = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  };
  return (
    <div className="rounded-md bg-bg-secondary/40 border border-border-default p-2">
      <div className={`font-mono text-lg ${TONE[tone] || "text-text-primary"}`}>
        {(value ?? 0).toLocaleString("en-IN")}
      </div>
      <div className="text-text-secondary text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}
